import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Sidesheet,
  SidesheetProps,
  Typography,
} from '@beeline/design-system-react/extracted-styles';

import clsx from 'clsx';
import { AdministratorBuildingSidesheetPanel } from 'src/pages/AdministratorPage/AdministratorBuildingSidesheet/AdministratorBuildingSidesheetPanel';
import { AdministratorBuildingSidesheetTitle } from 'src/pages/AdministratorPage/AdministratorBuildingSidesheet/AdministratorBuildingSidesheetTitle';
import { AdministratorFloorSidesheetPanel } from 'src/pages/AdministratorPage/AdministratorFloorSidesheet/AdministratorFloorSidesheetPanel';
import { AdministratorFloorSidesheetTitle } from 'src/pages/AdministratorPage/AdministratorFloorSidesheet/AdministratorFloorSidesheetTitle';
import { AdminSidesheetPanel } from 'src/pages/AdministratorPage/AdministratorSidesheet/AdminSidesheetPanel';
import { AdminSidesheetTitle } from 'src/pages/AdministratorPage/AdministratorSidesheet/AdminSidesheetTitle';
import { AdministratorTownSidesheetPanel } from 'src/pages/AdministratorPage/AdministratorTownSidesheet/AdministratorTownSidesheetPanel';
import { AdministratorTownSidesheetTitle } from 'src/pages/AdministratorPage/AdministratorTownSidesheet/AdministratorTownSidesheetTitle';
import { AdministratorUserPermissionsSidesheetPanel } from 'src/pages/AdministratorPage/AdministratorUserPermissions/AdministratorUserPermissionsSidesheetPanel';
import { DeskBookingFiltersSidesheetPanel } from 'src/pages/DeskBookingPage/DeskBookingFilters/DeskBookingFiltersSidesheetPanel';
import { DeskBookingSidesheetPanel } from 'src/pages/DeskBookingPage/DeskBookingSidesheet/DeskBookingSidesheetPanel';
import { DeskBookingSidesheetTitle } from 'src/pages/DeskBookingPage/DeskBookingSidesheet/DeskBookingSidesheetTitle';
import { DeskOfficeSidesheetContent } from 'src/pages/DeskBookingPage/DeskOffice/DeskOfficeSidesheetContent';
import { FeedbackSidesheetPanel } from 'src/pages/FeedbackPage/FeedbackList/FeedbackSidesheet/FeedbackSidesheetPanel';
import { MyBookingsFiltersSidesheetPanel } from 'src/pages/MyBookingsPage/MyBookingsFilters/MyBookingsFiltersSidesheet/MyBookingsFiltersSidesheetPanel';
import { ParkingBookingSidesheetPanel } from 'src/pages/ParkingPage/ParkingBookingSidesheet/ParkingBookingSidesheetPanel';
import { ParkingBookingSidesheetTitle } from 'src/pages/ParkingPage/ParkingBookingSidesheet/ParkingBookingSidesheetTitle';
import { ParkingOfficeSidesheetContent } from 'src/pages/ParkingPage/ParkingOffice/ParkingOfficeSidesheetContent';
import { RoomBookingFiltersSidesheetPanel } from 'src/pages/RoomBookingPage/RoomBookingFilters/RoomBookingFiltersSidesheetPanel';
import { RoomBookingSidesheetPanel } from 'src/pages/RoomBookingPage/RoomBookingSidesheet/RoomBookingSidesheetPanel';
import { RoomBookingSidesheetTitle } from 'src/pages/RoomBookingPage/RoomBookingSidesheet/RoomBookingSidesheetTitle';
import { RoomOfficeSidesheetContent } from 'src/pages/RoomBookingPage/RoomOffice/RoomOfficeSidesheetContent';
import { SearchFiltersSidesheetPanel } from 'src/pages/SearchPage/SearchFilters/SearchFiltersSidesheet/SearchFiltersSidesheetPanel';
import { getSidesheet } from 'src/store/sidesheet/selectors';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

interface SidesheetComponents {
  Title: FC;
  Content: FC;
}

const sidesheetContents: Record<string, SidesheetComponents> = {
  deskBooking: {
    Title: DeskBookingSidesheetTitle,
    Content: DeskBookingSidesheetPanel,
  },
  deskOffice: {
    Title: () => <Typography variant='h5'>Настройки города/офиса</Typography>,
    Content: DeskOfficeSidesheetContent,
  },
  desksFilters: {
    Title: () => <Typography variant='h5'>Фильтры</Typography>,
    Content: DeskBookingFiltersSidesheetPanel,
  },
  roomBooking: {
    Title: RoomBookingSidesheetTitle,
    Content: RoomBookingSidesheetPanel,
  },
  roomOffice: {
    Title: () => <Typography variant='h5'>Настройки города/офиса</Typography>,
    Content: RoomOfficeSidesheetContent,
  },
  roomsFilters: {
    Title: () => <Typography variant='h5'>Фильтры</Typography>,
    Content: RoomBookingFiltersSidesheetPanel,
  },
  parkingBooking: {
    Title: ParkingBookingSidesheetTitle,
    Content: ParkingBookingSidesheetPanel,
  },
  parkingOffice: {
    Title: () => <Typography variant='h5'>Настройки города/офиса</Typography>,
    Content: ParkingOfficeSidesheetContent,
  },
  adminBookingObject: {
    Title: AdminSidesheetTitle,
    Content: AdminSidesheetPanel,
  },
  permissions: {
    Title: () => <Typography variant='h5'>Настройки прав</Typography>,
    Content: AdministratorUserPermissionsSidesheetPanel,
  },
  myBookingsFilters: {
    Title: () => <Typography variant='h5'>Фильтры</Typography>,
    Content: MyBookingsFiltersSidesheetPanel,
  },
  searchFilters: {
    Title: () => <Typography variant='h5'>Фильтры</Typography>,
    Content: SearchFiltersSidesheetPanel,
  },
  feedback: {
    Title: () => <Typography variant='h5'>Обращение</Typography>,
    Content: FeedbackSidesheetPanel,
  },
  adminTown: {
    Title: AdministratorTownSidesheetTitle,
    Content: AdministratorTownSidesheetPanel,
  },
  adminBuilding: {
    Title: AdministratorBuildingSidesheetTitle,
    Content: AdministratorBuildingSidesheetPanel,
  },
  adminFloor: {
    Title: AdministratorFloorSidesheetTitle,
    Content: AdministratorFloorSidesheetPanel,
  },
};

export interface SidesheetSlotProps
  extends Omit<SidesheetProps, 'content' | 'isOpen' | 'onClose' | 'title'> {
  id: string;
}

export const SidesheetSlot: FC<SidesheetSlotProps> = ({
  id,
  children,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const { opened, current, noTransition } = useSelector(getSidesheet);

  const { Content, Title } = useMemo(() => {
    const components =
      current && opened === id ? sidesheetContents[current] : null;
    return { Content: components?.Content, Title: components?.Title };
  }, [current, id, opened]);

  const close = useCallback(() => {
    dispatch(sidesheetActions.close());
  }, [dispatch]);

  return (
    <Sidesheet
      content={Content ? <Content /> : null}
      isOpen={opened === id}
      onClose={close}
      title={Title ? <Title /> : null}
      data-testid='globalSidesheet'
      className={clsx(noTransition && 'no_transition')}
      {...rest}
    >
      {children}
    </Sidesheet>
  );
};
